"use client";

import { useEffect, useRef } from "react";

/**
 * Hero parallax (`catalogo/logic/01.js:37-52`).
 *
 * The stage is centred against the viewport, not tied to absolute scroll position: `d` is the
 * signed distance from the frame's own centre to the viewport's centre, clamped to the frame's
 * height so the layer never travels further than its own `scale(1.20)` slack covers. The 0.08
 * factor and the one-decimal string are literal from the reference — a different rounding would
 * make the CSS variable churn on sub-pixel scroll and cost more than it moves.
 */

const PARALLAX_FACTOR = 0.08;

export function useCatalogHeroParallax() {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const prefersReducedMotionRef = useRef(false);

  useEffect(() => {
    prefersReducedMotionRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    function update() {
      if (prefersReducedMotionRef.current) return;

      const frame = frameRef.current;
      if (frame === null) return;

      const height = frame.offsetHeight || 600;
      const rect = frame.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 800;
      const rel = rect.top + height / 2 - viewportHeight / 2;
      const displacement = Math.min(Math.max(-rel, -height), height);

      frame.style.setProperty("--heroPY", `${(displacement * PARALLAX_FACTOR).toFixed(1)}px`);
    }

    function queue() {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        update();
      });
    }

    window.addEventListener("scroll", queue, { passive: true });
    window.addEventListener("resize", queue);
    queue();

    return () => {
      window.removeEventListener("scroll", queue);
      window.removeEventListener("resize", queue);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return frameRef;
}
